import { Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter, map, pairwise } from 'rxjs/operators';

interface ScrollRestoreRule {
  from: string;
  to: string;
}

interface ScrollRestoreData {
  top: number;
  resultPage?: number;
  articlePage?: number;
}

/**
 * Scroll restoration rules should be set here:
 *
 * Whenever a navigation happens from a 'from' route to a 'to' route, the previous scroll position of the 'to' route will be restored.
 * Restoration happens after the 'to' page called the 'pageReady()' method to ensure that its content is rendered.
 *
 * '*' wildcard is supported inside the URLs.
 */
const SCROLL_RESTORE_RULES: ScrollRestoreRule[] = [
  { from: '/articles/*', to: '/member-benefits/search' },
  { from: '/dining/restaurant/*', to: '/dining/search' },
  { from: '/dining/restaurant/*', to: '/landing' },
  { from: '/dining/restaurant/*', to: '/dining/results' },
  { from: '/dining/search', to: '/landing' },
  { from: '/dining/search', to: '/dining/search' },
  { from: '/entertainment/events/*', to: '/entertainment/search' },
  { from: '/entertainment/events/*', to: '/landing' },
  { from: '/entertainment/search/*', to: '/landing' },
  { from: '/member-benefits/details/*', to: '/member-benefits/search' },
  { from: '/member-benefits/details/*', to: '/inspiration' },
  { from: '/member-benefits/details/*', to: '/landing' },
  { from: '/member-benefits/search', to: '/landing' },
  { from: '/member-events/details/*', to: '/member-events/search' },
  { from: '/member-events/details/*', to: '/landing' },
  { from: '/member-events/search/*', to: '/landing' },
  { from: '/member-events/search/*', to: '/member-events/search/*' },
  { from: '/partners', to: '/landing' },
  { from: '/travel', to: '/landing' },
  { from: '/travel/car-hire-results/details', to: '/travel/car-hire-results' },
  { from: '/travel/flight-results/*', to: '/travel/flight-results' },
  { from: '/travel/hotel-results/details/*', to: '/travel/hotel-results' },
  { from: '/travel/hotel-results/details/*', to: '/travel/search' },
  { from: '/travel/hotel-results/details/*', to: '/landing' },
  { from: '/inspiration/*', to: '/entertainment/search' },
  { from: '/inspiration/*', to: '/member-benefits/search' },
  { from: '/inspiration/*', to: '/member-events/search' },
  { from: '/inspiration/*', to: '/dining/search' },
  { from: '/inspiration/*', to: '/landing' },
  { from: '/inspiration/*', to: '/travel/search' },
  { from: '/inspiration/publication/*', to: '/inspiration/search' },
  { from: '/inspiration/article/*', to: '/inspiration/search' },
  { from: '/travel/activities-results/details/*', to: '/travel/activities-results' },
  { from: '/travel/hotel-results/details/*', to: '/travel/hotel-results' },
  { from: '/entertainment/results/details/*', to: '/entertainment/search' },
  { from: '/entertainment/results/details/*', to: '/landing' },
];

@Injectable({ providedIn: 'root' })
export class ScrollService {
  // Stores ScrollRestoreData's for each page
  private scrollRestoreDataMap: { [pageUrl: string]: ScrollRestoreData };
  // Contains ScrollRestoreData if the current state met any of the scroll restore rules, otherwise null
  private currentScrollRestoreData$: BehaviorSubject<ScrollRestoreData>;
  private currentUrl: string;
  private offset: number;

  constructor(private router: Router) {
    this.scrollRestoreDataMap = {};
    this.currentScrollRestoreData$ = new BehaviorSubject(null);

    this.router.events.pipe(filter<NavigationEnd>((event) => event instanceof NavigationEnd)).subscribe((event) => {
      [this.currentUrl] = event.urlAfterRedirects.split('?');
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
      if (!router.navigated) {
        const localRestoreData = JSON.parse(localStorage.getItem('scrollRestoreData') || '{}');
        if (localRestoreData[location.pathname]) {
          this.setScrollRestoreData(localRestoreData[location.pathname]);
          this.currentScrollRestoreData$.next(this.scrollRestoreDataMap[this.currentUrl]);
        }
      } else {
        this.setScrollRestoreData({ top: window.scrollY });
      }
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        pairwise(),
        map(([prev, curr]: [NavigationEnd, NavigationEnd]) => {
          const rule: ScrollRestoreRule = SCROLL_RESTORE_RULES.find(
            (x: ScrollRestoreRule) => prev.urlAfterRedirects.match(x.from) && curr.urlAfterRedirects.match(x.to)
          );

          return rule ? this.scrollRestoreDataMap[rule.to] : null;
        })
      )
      .subscribe(this.currentScrollRestoreData$);
  }

  /**
   * Sets an offset used when scrolling to a position.
   * Offset should be set to the height of sticky elements e.g. navbars, headers.
   *
   * @param offset Offset in pixels
   */
  public setOffset(offset: number) {
    this.offset = offset;
  }

  /**
   * Scrolls to the given position, considers offset if the offset param is true.
   *
   * @param left Left position in pixels
   * @param top Top position in pixels
   * @param useOffset True if scroll position should consider the current offset
   */
  public scrollTo(left: number, top: number, useOffset = true) {
    const offsetedTop = top - (useOffset ? this.offset : 0);

    if ('scrollBehavior' in document.documentElement.style) {
      window.scrollTo({ left, top: offsetedTop, behavior: 'smooth' });
    } else {
      window.scrollTo(left, offsetedTop);
    }
  }

  /**
   * Called by the page component when its content is rendered.
   * Restores previous scrollPosition if needed based on the scrollRestoreRules.
   */
  public pageReady() {
    const currentScrollRestoreData = this.getCurrentScrollRestoreData();

    if (currentScrollRestoreData) {
      this.scrollTo(0, currentScrollRestoreData.top, false);
    }
  }

  /**
   * Sets ScrollRestoreData for the current page e.g. How many pages were loaded, how many article pages were loaded.
   *
   * @param data Partial ScrollRestoreData to save
   */
  public setScrollRestoreData(data: Partial<ScrollRestoreData>) {
    if (!this.currentUrl) {
      this.currentUrl = location.pathname;
    }
    this.scrollRestoreDataMap[this.currentUrl] = {
      ...this.scrollRestoreDataMap[this.currentUrl],
      ...data,
    };
  }

  public saveSnapshot(data: Partial<ScrollRestoreData>) {
    if (!this.currentUrl) {
      this.currentUrl = location.pathname;
    }

    localStorage.setItem(
      'scrollRestoreData',
      JSON.stringify({
        [this.currentUrl]: {
          ...this.scrollRestoreDataMap[this.currentUrl],
          ...data,
        },
      })
    );
  }

  /**
   * Returns ScrollRestoreData for the CURRENT page. Null if scrollRestoreRules were not met.
   */
  public getCurrentScrollRestoreData(): ScrollRestoreData {
    const storedScrollData = JSON.parse(localStorage.getItem('scrollRestoreData'));
    return this.currentScrollRestoreData$.value || (storedScrollData && storedScrollData[this.currentUrl]);
  }

  /**
   * Reset ScrollRestoreData for the CURRENT page.
   */
  public resetScrollRestoreData() {
    this.setScrollRestoreData({ resultPage: 1, top: 0 });
    this.currentScrollRestoreData$.next(this.scrollRestoreDataMap[this.currentUrl]);
  }
}
